import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { shortKey } from '../../utils/format';

interface ChatMessage {
  wallet: string;
  displayName: string;
  message: string;
  timestamp: number;
}

interface Props {
  messages: ChatMessage[];
  onSend: (message: string) => Promise<void> | void;
  disabled?: boolean;
  currentWallet?: string;
}

const formatTime = (timestamp: number) => {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp));
};

export const MatchChat: React.FC<Props> = ({ messages, onSend, disabled, currentWallet }) => {
  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => a.timestamp - b.timestamp),
    [messages],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = input.trim();
      if (!trimmed) {
        return;
      }
      try {
        await onSend(trimmed);
        setInput('');
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to send chat message right now.';
        toast.error(message);
      }
    },
    [input, onSend],
  );

  return (
    <section className="flex h-full flex-col gap-3 rounded-2xl border border-white/5 bg-white/5 p-4">
      <header>
        <p className="text-xs uppercase tracking-[0.4em] text-neon-500">Lobby Chat</p>
      </header>
      <div
        ref={listRef}
        className="flex-1 space-y-3 overflow-y-auto rounded-xl bg-solana-900/40 p-3 text-sm text-white/80"
      >
        {sortedMessages.length === 0 ? (
          <p className="text-xs text-white/50">No messages yet. Say hello!</p>
        ) : (
          sortedMessages.map((msg, index) => {
            const mine = msg.wallet === currentWallet;
            return (
              <div key={`${msg.timestamp}-${msg.wallet}-${index}`} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs font-semibold ${mine ? 'text-neon-300' : 'text-white/70'}`}
                  >
                    {msg.displayName || shortKey(msg.wallet)}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-white/40">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
                <p
                  className={`rounded-lg px-3 py-2 text-sm ${mine ? 'bg-neon-600/20 text-white' : 'bg-white/10'}`}
                >
                  {msg.message}
                </p>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={disabled ? 'Connect your wallet to chat' : 'Type a message…'}
          maxLength={280}
          className="flex-1 rounded-full border border-white/10 bg-solana-800/70 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-neon-500 focus:outline-none"
          disabled={disabled}
        />
        <button
          type="submit"
          className="rounded-full bg-neon-500 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-solana-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={disabled || input.trim().length === 0}
        >
          Send
        </button>
      </form>
    </section>
  );
};
